import React, { useEffect, useMemo, useState } from 'react';
import s from './Upgrade.module.css';
import { tryFetchWithAuth } from 'utils/fetchWithAuth';
import { useAuth } from 'contexts/AuthContext';
import { useCredits } from 'contexts/CreditsContext';

const CREDIT_UNIT_PRICE_CENTS = 0.5;
const PRESET_PACKS = [
    { credits: 1000, label: '1,000 credits' },
    { credits: 5000, label: '5,000 credits' },
    { credits: 25000, label: '25,000 credits' },
    { credits: 100000, label: '100,000 credits' },
];
const CALENDLY_URL = 'https://calendly.com/quadratik/b2b-call-1';

const formatUsd = (cents) => `$${(cents / 100).toFixed(2)}`;
const formatNum = (n) => n.toLocaleString();

const UpgradePage = () => {
    const { user, profile } = useAuth();
    const { balance, isUnlimited, refresh } = useCredits();

    const [selectedPack, setSelectedPack] = useState(PRESET_PACKS[1].credits);
    const [customCredits, setCustomCredits] = useState('');
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('session_id')) {
            setPurchaseSuccess(true);
            refresh();
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [refresh]);

    const customCreditsNum = parseInt(customCredits, 10);
    const usingCustom = !Number.isNaN(customCreditsNum) && customCreditsNum > 0;
    const creditsToBuy = usingCustom ? customCreditsNum : selectedPack;
    const priceCents = useMemo(
        () => Math.round(creditsToBuy * CREDIT_UNIT_PRICE_CENTS),
        [creditsToBuy]
    );

    const handleBuyCredits = async () => {
        setCheckoutError(null);

        if (!user) {
            window.location.href = '/loginsignup';
            return;
        }
        if (!creditsToBuy || creditsToBuy < 1000 || creditsToBuy > 1_000_000 || creditsToBuy % 100 !== 0) {
            setCheckoutError('Credits must be a multiple of 100, between 1,000 and 1,000,000.');
            return;
        }

        setCheckoutLoading(true);
        const { data, error: fetchError } = await tryFetchWithAuth('/credits/checkout', {
            method: 'POST',
            body: JSON.stringify({ credits: creditsToBuy }),
        });
        setCheckoutLoading(false);

        if (fetchError || !data?.url) {
            setCheckoutError(fetchError || 'Could not start checkout.');
            return;
        }
        window.location.href = data.url;
    };

    // After returning from Stripe we want the balance to reflect the purchase.
    // The caller can refresh the credits context by hitting this page; it auto-refreshes on mount via CreditsProvider.

    if (!user || !profile) {
        return <div className={s.loadingContainer}>Loading...</div>;
    }

    return (
        <div className={s.upgradePage}>
            <div className={s.inner}>
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Quadratik logo" className={s.logo} />
                <h1 className={s.upgradeHeader}>Upgrade</h1>
                <p className={s.subHeader}>Pay as you go, or go unlimited.</p>
            </div>
            {purchaseSuccess && (
                <div className={s.successBanner}>
                    Purchase successful — your credits have been added.
                </div>
            )}
            <div className={s.balanceStrip}>
                {isUnlimited
                    ? 'You are on the Unlimited plan — no credits required.'
                    : <>Current balance: <strong>{formatNum(balance)}</strong> credits</>
                }
                <button className={s.refreshButton} onClick={refresh}>Refresh</button>
            </div>

            <div className={s.cardRow}>
                {/* BUY CREDITS CARD (PAYG) */}
                <div className={s.card}>
                    <div className={s.cardTitle}>Buy Credits</div>
                    <div className={s.cardSubtitle}>Pay only for what you unlock. Credits never expire.</div>

                    <div className={s.priceList}>
                        <div className={s.priceRow}><span>Email unlock</span><span>1 credit</span></div>
                        <div className={s.priceRow}><span>Mobile unlock</span><span>5 credits</span></div>
                        <div className={s.priceRow}><span>Credit price</span><span>$0.005</span></div>
                    </div>

                    <div className={s.packGrid}>
                        {PRESET_PACKS.map(pack => (
                            <div
                                key={pack.credits}
                                className={`${s.packOption} ${!usingCustom && selectedPack === pack.credits ? s.packSelected : ''}`}
                                onClick={() => { setSelectedPack(pack.credits); setCustomCredits(''); }}
                            >
                                <div className={s.packLabel}>{pack.label}</div>
                                <div className={s.packPrice}>{formatUsd(pack.credits * CREDIT_UNIT_PRICE_CENTS)}</div>
                            </div>
                        ))}
                    </div>

                    <div className={s.customRow}>
                        <label className={s.customLabel}>Custom amount (min 1,000, multiples of 100)</label>
                        <input
                            className={s.customInput}
                            type="number"
                            min="1000"
                            max="1000000"
                            step="100"
                            placeholder="e.g. 2500"
                            value={customCredits}
                            onChange={(e) => setCustomCredits(e.target.value)}
                        />
                    </div>

                    <div className={s.summaryRow}>
                        <span>{formatNum(creditsToBuy)} credits</span>
                        <span className={s.summaryPrice}>{formatUsd(priceCents)}</span>
                    </div>

                    {checkoutError && <div className={s.errorText}>{checkoutError}</div>}

                    <button
                        className={s.primaryButton}
                        disabled={checkoutLoading}
                        onClick={handleBuyCredits}
                    >
                        {checkoutLoading ? 'Redirecting…' : `Buy ${formatNum(creditsToBuy)} credits`}
                    </button>
                </div>

                {/* UNLIMITED CARD */}
                <div className={s.card}>
                    <div className={s.cardTitle}>Unlimited</div>
                    <div className={s.cardSubtitle}>Unlimited unlocks and exports for high-volume teams. Custom pricing.</div>

                    <div className={s.priceList}>
                        <div className={s.priceRow}><span>Unlocks</span><span>Unlimited</span></div>
                        <div className={s.priceRow}><span>Exports</span><span>Unlimited</span></div>
                        <div className={s.priceRow}><span>Onboarding</span><span>Dedicated</span></div>
                    </div>

                    <ul className={s.featureList}>
                        <li>Full access to 220M+ verified B2B contacts</li>
                        <li>Priority support and integration help</li>
                        <li>Annual invoicing available</li>
                    </ul>

                    <a
                        className={s.primaryButton}
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Book a call
                    </a>
                </div>
            </div>
        </div>
    );
};

export default UpgradePage;
